import { useHistory } from "react-router";

import { Info } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken, Tooltip } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { HelpCenter } from "constants/help-center";
import { ratingToColorMap, ratingToIconMap, ratingToTextMap } from "constants/rating";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { Rating, TicketStatus } from "interfaces/entities/ticket";
import { AutomationsRoutes } from "routes/automations/constants";
import { withVar } from "themes/helpers";

function getRatingIcon(rating: Rating): React.ReactElement {
  return <Icon source={ratingToIconMap[rating]} size="small" customColor={ratingToColorMap[rating]} />;
}

export const TicketRatingLabel: React.FC<{
  ratingRequestSent: boolean;
  rating: Rating | null;
  status: TicketStatus;
}> = ({ ratingRequestSent, rating, status }) => {
  const history = useHistory();
  const showTooltip = rating == null;
  const showExternalAppLink = isInIframe();

  let labelText: React.ReactNode = "";
  if (rating) {
    labelText = (
      <>
        {ratingToTextMap[rating]} {getRatingIcon(rating)}
      </>
    );
  } else if (status === TicketStatus.Closed) {
    labelText = "Not applicable";
  } else {
    labelText = ratingRequestSent ? "Request sent" : "Not rated";
  }

  return (
    <>
      {labelText}
      {showTooltip && (
        <Tooltip
          kind="invert"
          placement="left"
          triggerRenderer={
            <span className="tooltip">
              <Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicPrimary)} />
            </span>
          }
        >
          Feedback requests allow measuring the customer satisfaction. This request is sent according to the settings in
          the rules section.
          <Flex gap={withVar(SpacingToken.Spacing3)}>
            <Button
              kind="link-inverted"
              onClick={() =>
                showExternalAppLink ? window.open(AutomationsRoutes.Rules) : history.push(AutomationsRoutes.Rules)
              }
            >
              Go to rules
            </Button>
            <Button
              target="_blank"
              kind="link-inverted"
              href={HelpCenter.WHAT_IS_TICKET_RATING}
              onClick={() => trackEngagement("Ticket rating: Learn more clicked", EventPlace.Tickets)}
            >
              Learn more
            </Button>
          </Flex>
        </Tooltip>
      )}
    </>
  );
};
